import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Pressable, View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { Audio } from 'expo-av';
import { supabase } from '../../lib/supabase';
import { apiFetch } from '../../lib/api';
import { GlassFill } from '../glass';
import { PromptField } from './PromptField';
import type { OrderWidgetProps } from './types';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../../v10/theme';

interface JournalPrompt {
  id: string;
  text: string;
  type: 'text' | 'dropdown';
  options?: string[];
}

interface CustomJournalEntryType {
  id: string;
  title: string;
  prompts: JournalPrompt[];
}

interface EpicEpisode {
  id: string;
  userId: string;
  date: string;
  episodeNumber: number;
  title: string;
  skin: string;
  script: string;
  editedScript?: string | null;
  unresolvedQuestion: string;
  audioPath?: string | null;
  audioStatus: 'none' | 'pending' | 'ready' | 'error' | 'unconfigured';
  status: 'generating' | 'ready' | 'error';
  createdAt: string;
  updatedAt: string;
}

interface EpicGenerateResponse {
  episode: EpicEpisode | null;
  archive: Array<{ id: string; date: string; title: string; episodeNumber: number; audioPath?: string | null }>;
}

function dateKeyOf(d: Date): string {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ClioWidget({ userId, gear, data, onOpen, onEngage }: OrderWidgetProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedStatus, setSavedStatus] = useState<string | null>(null);
  const [entryTypes, setEntryTypes] = useState<CustomJournalEntryType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True once the user has typed, so a late-arriving load can't clobber them. */
  const touched = useRef(false);

  const entryType = entryTypes.find((t) => t.id === typeId) ?? entryTypes[0] ?? null;

  // EPIC episode state
  const [epicEpisode, setEpicEpisode] = useState<EpicEpisode | null>(null);
  const [epicLoading, setEpicLoading] = useState(false);
  const [epicAudioPlaying, setEpicAudioPlaying] = useState(false);
  const epicSoundRef = useRef<Audio.Sound | null>(null);

  // Load entry types from user settings on mount
  useEffect(() => {
    if (!userId) {
      setLoadingTypes(false);
      return;
    }
    const loadEntryTypes = async () => {
      try {
        const { data: settings, error: err } = await supabase
          .from('user_settings')
          .select('custom_journal_entry_types')
          .eq('user_id', userId)
          .maybeSingle();

        if (err) throw err;

        const types = Array.isArray(settings?.custom_journal_entry_types)
          ? (settings.custom_journal_entry_types as CustomJournalEntryType[])
          : [];
        setEntryTypes(types);
        setTypeId((prev) => prev ?? types[0]?.id ?? null);
      } catch (e) {
        console.error('[ClioWidget] Failed to load entry types:', e);
        setEntryTypes([]);
      } finally {
        setLoadingTypes(false);
      }
    };
    void loadEntryTypes();
  }, [userId]);

  // Pull today's already-saved entry back into the field, so returning to the
  // dashboard shows what you wrote (and keeps the box green) instead of an
  // empty red one. Re-runs when the selected type changes.
  useEffect(() => {
    if (!userId || !entryType) return;
    const promptId = entryType.prompts[0]?.id;
    if (!promptId) return;

    let cancelled = false;
    const loadToday = async () => {
      try {
        const { data: row, error } = await supabase
          .from('journal_entries')
          .select('answers')
          .eq('user_id', userId)
          .eq('date', dateKeyOf(new Date()))
          .eq('entry_type_id', entryType.id)
          .maybeSingle();
        if (error) throw error;
        if (cancelled || touched.current) return;
        const answers = (row?.answers ?? {}) as Record<string, unknown>;
        const saved = answers[promptId];
        setText(typeof saved === 'string' ? saved : '');
      } catch (e) {
        console.error("[ClioWidget] Failed to load today's entry:", e);
      }
    };
    void loadToday();
    return () => {
      cancelled = true;
    };
  }, [userId, entryType?.id]);

  // Load EPIC episode on mount
  useEffect(() => {
    if (!userId) {
      setEpicLoading(false);
      return;
    }

    let cancelled = false;
    const loadEpic = async () => {
      try {
        setEpicLoading(true);

        // Get today's date (yyyy-MM-dd)
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        // Fetch today's episode
        const data = await apiFetch<EpicGenerateResponse>(`/api/epic/generate?date=${todayStr}`);
        if (cancelled) return;

        if (data.episode) {
          setEpicEpisode(data.episode);
        } else if (data.archive && data.archive.length > 0) {
          // No episode today — fall back to the most recent archived episode.
          const latest = [...data.archive].sort((a, b) => b.date.localeCompare(a.date))[0];
          const recent = await apiFetch<EpicGenerateResponse>(`/api/epic/generate?date=${latest.date}`);
          if (cancelled) return;
          setEpicEpisode(recent.episode || null);
        } else {
          setEpicEpisode(null);
        }
      } catch (e) {
        // No episode surface to fail into — the button just stays greyed out.
        console.error('[ClioWidget] Failed to load episode:', e);
        if (!cancelled) setEpicEpisode(null);
      } finally {
        if (!cancelled) setEpicLoading(false);
      }
    };

    loadEpic();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Cleanup sound on unmount
  useEffect(() => {
    return () => {
      if (epicSoundRef.current) {
        epicSoundRef.current.unloadAsync().catch(() => {});
      }
    };
  }, []);

  const handleEpicPlayAudio = useCallback(async () => {
    if (!epicEpisode?.audioPath) return;

    try {
      if (epicSoundRef.current) {
        const status = await epicSoundRef.current.getStatusAsync();
        if (status.isLoaded && status.isPlaying) {
          await epicSoundRef.current.pauseAsync();
          setEpicAudioPlaying(false);
          return;
        }
      }

      setEpicAudioPlaying(true);
      const sound = new Audio.Sound();
      epicSoundRef.current = sound;

      await sound.loadAsync({ uri: epicEpisode.audioPath });
      await sound.playAsync();

      // Set up completion handler
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) {
          setEpicAudioPlaying(false);
        }
      });
    } catch (e) {
      console.error('[ClioWidget] Failed to play episode audio:', e);
      setEpicAudioPlaying(false);
    }
  }, [epicEpisode?.audioPath]);

  useEffect(() => {
    return () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    };
  }, []);

  const handleSave = async () => {
    if (!userId || !text.trim() || saving || !entryType) return;

    setSaving(true);
    try {
      const today = dateKeyOf(new Date());
      const now = new Date().toISOString();

      const firstPromptId = entryType.prompts[0]?.id;

      if (!firstPromptId) {
        setSavedStatus('Error: No prompts in entry type');
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSavedStatus(null), 2000);
        setSaving(false);
        return;
      }

      // Try to find existing entry for today with this entry type
      const { data: existing, error: fetchErr } = await supabase
        .from('journal_entries')
        .select('id, answers')
        .eq('user_id', userId)
        .eq('date', today)
        .eq('entry_type_id', entryType.id)
        .maybeSingle();

      if (fetchErr) throw fetchErr;

      // This widget only edits the first prompt. Start from whatever is already
      // stored so answers to the type's other prompts (filled in inside the
      // CLIO tool) survive a save from here.
      const answers: Record<string, string> = {};
      for (const p of entryType.prompts) answers[p.id] = '';
      Object.assign(answers, (existing?.answers ?? {}) as Record<string, string>);
      answers[firstPromptId] = text;

      if (existing?.id) {
        // Update existing
        const { error: updateErr } = await supabase
          .from('journal_entries')
          .update({
            answers,
            updated_at: now,
          })
          .eq('id', existing.id);

        if (updateErr) throw updateErr;
      } else {
        // Insert new
        const { error: insertErr } = await supabase
          .from('journal_entries')
          .insert({
            user_id: userId,
            date: today,
            entry_type_id: entryType.id,
            answers,
            created_at: now,
            updated_at: now,
          });

        if (insertErr) throw insertErr;
      }

      // The entry IS this band's engagement for the day (green dot).
      onEngage?.();

      // Field deliberately keeps its text — clearing it would flip the box
      // back to the red "still owed" state right after a successful save.
      setSavedStatus('Saved ✓');
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSavedStatus(null), 2000);
    } catch (e) {
      console.error('[ClioWidget] Save failed:', e);
      setSavedStatus('Error');
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSavedStatus(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loadingTypes) {
    return (
      <View style={styles.container}>
        <ActivityIndicator color={c.primary} size="small" />
      </View>
    );
  }

  if (entryTypes.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyText}>No journal entry types configured</Text>
        <Pressable style={styles.openPill} onPress={onOpen}>
          <Text style={styles.openPillText}>Configure in CLIO</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Label row: plain text + today's EPIC episode as a bare play button.
          Episode number/title/date live in the EPIC tool, not here. */}
      <View style={styles.header}>
        <Text style={styles.headerText}>New Journal Entry</Text>
        {epicLoading ? (
          <ActivityIndicator color={c.blueText} size="small" />
        ) : (
          <Pressable
            style={[
              styles.epicPlayButton,
              !epicEpisode?.audioPath && styles.epicPlayButtonDisabled,
              epicAudioPlaying && styles.epicPlayButtonActive,
            ]}
            onPress={handleEpicPlayAudio}
            disabled={!epicEpisode?.audioPath}
          >
            <Text style={styles.epicPlayButtonText}>{epicAudioPlaying ? '⏸' : '▶'}</Text>
          </Pressable>
        )}
      </View>

      {/* Field + send icon, plus a type dropdown on the same row when the user
          has more than one journal entry type configured. */}
      <PromptField
        value={text}
        onChangeText={(next) => {
          touched.current = true;
          setText(next);
        }}
        onSubmit={handleSave}
        placeholder={entryType?.title ?? 'Clio Entry'}
        maxLength={500}
        minHeight={90}
        submitting={saving}
        accessory={
          entryTypes.length > 1 ? (
            <View style={styles.typePickerHost}>
              <Pressable style={styles.typePill} onPress={() => setTypeMenuOpen((o) => !o)}>
                <Text style={styles.typePillText} numberOfLines={1}>
                  {entryType?.title ?? 'Type'}
                </Text>
                <Text style={styles.typePillCaret}>{typeMenuOpen ? '▴' : '▾'}</Text>
              </Pressable>
              {typeMenuOpen && (
                <View style={styles.typeMenu}>
<GlassFill radius={radii.tile} />
                  {entryTypes.map((t) => (
                    <Pressable
                      key={t.id}
                      style={styles.typeMenuItem}
                      onPress={() => {
                        setTypeMenuOpen(false);
                        if (t.id === entryType?.id) return;
                        // Switching types loads that type's own entry, so let
                        // the loader win over anything typed for the old one.
                        touched.current = false;
                        setText('');
                        setTypeId(t.id);
                      }}
                    >
                      <Text
                        style={[
                          styles.typeMenuItemText,
                          t.id === entryType?.id && styles.typeMenuItemTextActive,
                        ]}
                        numberOfLines={1}
                      >
                        {t.title}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          ) : null
        }
      />

      {savedStatus && (
        <Text style={[styles.statusText, savedStatus === 'Error' && styles.statusError]}>
          {savedStatus}
        </Text>
      )}

      {/* Journal written today indicator */}
      {data.journalWrittenToday && (
        <Text style={styles.journalWrittenText}>journal written today</Text>
      )}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  // No wrapper card — label, field and send icon sit bare on the band tint.
  container: {
    gap: 6,
    maxHeight: 380,
  },
  emptyText: {
    fontSize: 12,
    color: c.textMuted,
    textAlign: 'center',
    marginBottom: 10,
  },
  openPill: {
    alignSelf: 'center',
    backgroundColor: c.primary,
    borderRadius: radii.pill,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  openPillText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: t.onGold,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 28,
  },
  headerText: {
    fontSize: 13,
    color: c.text,
  },
  // Journal-type dropdown, only rendered when >1 type exists. The menu opens
  // upward so the band's bottom edge can't clip it.
  typePickerHost: { position: 'relative', paddingBottom: 4 },
  typePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    maxWidth: 110,
    backgroundColor: c.inputFill,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: c.inputRim,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  typePillText: { flexShrink: 1, fontSize: 11, fontWeight: '700', color: c.text },
  typePillCaret: { fontSize: 9, color: c.slate },
  typeMenu: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: 4,
    minWidth: 140,
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: radii.tile,
    borderWidth: 1,
    borderColor: c.inputRim,
    paddingVertical: 4,
    zIndex: 20,
    ...neu.raisedSm,
  },
  typeMenuItem: { paddingHorizontal: 10, paddingVertical: 7 },
  typeMenuItemText: { fontSize: 12, color: c.text },
  typeMenuItemTextActive: { fontWeight: '800', color: c.primary },

  statusText: {
    fontSize: 10,
    color: c.primary,
    fontWeight: '600',
  },
  statusError: {
    color: c.danger,
  },
  journalWrittenText: {
    fontSize: 9,
    color: c.textMuted,
    marginTop: 6,
    fontStyle: 'italic',
  },

  // Today's EPIC episode — a bare round play button in the header row.
  epicPlayButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: c.lime,
    alignItems: 'center',
    justifyContent: 'center',
    ...neu.glowPrimary,
  },
  epicPlayButtonActive: {
    opacity: 0.8,
  },
  epicPlayButtonDisabled: {
    backgroundColor: c.glass,
    opacity: 0.45,
  },
  epicPlayButtonText: {
    fontWeight: '900',
    color: c.blueText,
    fontSize: 12,
  },
}),
);
